import { useEffect, useRef, useState } from 'react';
import { useCMS } from '@/hooks/useCMS';

const DEFAULT = {
  left_label: 'კონსულტაცია',
  left_heading_line1: 'დაიწყეთ',
  left_heading_line2: 'პროექტი',
  left_heading_line3: 'დღეს',
  left_subtext: 'ჩვენი სპეციალისტი 24 საათში დაგიკავშირდებათ და დაგეხმარებათ სწორი მასალების შერჩევაში.',
  left_phone: '+995 599 87 20 20',
  left_phone_href: 'tel:+995599872020',
  left_email: 'info@phoenix.ge',
  left_email_href: 'mailto:info@phoenix.ge',
  left_address: 'გელოვანის ქ. N13, თბილისი',
  left_image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20kitchen%20with%20premium%20stone%20countertops%20and%20high-end%20flooring%20materials%2C%20sleek%20cabinetry%2C%20professional%20construction%20quality%2C%20warm%20neutral%20tones%2C%20sophisticated%20minimalist%20interior%20design%2C%20cream%20and%20warm%20gray%20background&width=1600&height=900&seq=hero3&orientation=landscape',
  left_image_alt: 'Phoenix კონსულტაცია',
  form_url: 'https://readdy.ai/api/form/submit/phoenix-consultation',
  form_heading: 'უფასო კონსულტაცია',
  form_subtext: 'შეავსეთ ფორმა და ჩვენ დაგიკავშირდებით.',
  form_label_name: 'სახელი და გვარი',
  form_placeholder_name: 'გიორგი მჭედლიძე',
  form_label_phone: 'ტელეფონი',
  form_placeholder_phone: '+995 5XX XX XX XX',
  form_label_email: 'ელ-ფოსტა',
  form_placeholder_email: 'info@company.ge',
  form_label_message: 'პროექტის აღწერა',
  form_placeholder_message: 'მოგვიყევით თქვენი პროექტის შესახებ... (მაქს. 500 სიმბოლო)',
  form_btn_label: 'კონსულტაციის მოთხოვნა',
  form_btn_loading: 'იგზავნება...',
  form_thanks_title: 'გმადლობთ!',
  form_thanks_text: 'ჩვენი სპეციალისტი მალე დაგიკავშირდებათ.',
};

export default function CTASection() {
  const cms = useCMS('cta_section', DEFAULT);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch(cms.form_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={sectionRef} className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
      {/* Left — image with overlay text */}
      <div className="relative min-h-[400px] lg:min-h-0 overflow-hidden">
        <img src={cms.left_image} alt={cms.left_image_alt} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1C1C1C]/60"></div>
        <div className="reveal opacity-0 absolute inset-0 flex flex-col justify-end p-10 md:p-14">
          <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] block mb-4">{cms.left_label}</span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
            {cms.left_heading_line1}<br />{cms.left_heading_line2}<br />{cms.left_heading_line3}
          </h2>
          <p className="text-white/55 text-sm leading-relaxed max-w-sm mb-8">{cms.left_subtext}</p>
          <div className="flex flex-col gap-3">
            <a href={cms.left_phone_href} className="flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300 cursor-pointer group">
              <div className="w-8 h-8 border border-white/20 flex items-center justify-center group-hover:border-[#ec3145] transition-colors duration-300">
                <i className="ri-phone-line text-xs"></i>
              </div>
              <span className="text-sm font-medium">{cms.left_phone}</span>
            </a>
            <a href={cms.left_email_href} className="flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300 cursor-pointer group">
              <div className="w-8 h-8 border border-white/20 flex items-center justify-center group-hover:border-[#ec3145] transition-colors duration-300">
                <i className="ri-mail-line text-xs"></i>
              </div>
              <span className="text-sm font-medium">{cms.left_email}</span>
            </a>
            <div className="flex items-center gap-3 text-white/50">
              <div className="w-8 h-8 border border-white/10 flex items-center justify-center">
                <i className="ri-map-pin-line text-xs"></i>
              </div>
              <span className="text-sm">{cms.left_address}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="bg-[#F5F2EE] flex items-center justify-center p-10 md:p-14">
        <div className="reveal opacity-0 w-full max-w-md">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 border border-[#ec3145] flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-[#ec3145] text-2xl"></i>
              </div>
              <h3 className="font-serif text-2xl font-bold text-[#1C1C1C] mb-2">{cms.form_thanks_title}</h3>
              <p className="text-[#1C1C1C]/50 text-sm">{cms.form_thanks_text}</p>
            </div>
          ) : (
            <>
              <h3 className="font-serif text-3xl font-bold text-[#1C1C1C] mb-2">{cms.form_heading}</h3>
              <p className="text-[#1C1C1C]/45 text-sm mb-8">{cms.form_subtext}</p>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/40">{cms.form_label_name}</label>
                  <input type="text" name="name" required className="bg-white border border-[#1C1C1C]/10 text-[#1C1C1C] px-4 py-3 text-sm focus:outline-none focus:border-[#ec3145] transition-colors duration-300 placeholder-[#1C1C1C]/25" placeholder={cms.form_placeholder_name} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/40">{cms.form_label_phone}</label>
                  <input type="tel" name="phone" className="bg-white border border-[#1C1C1C]/10 text-[#1C1C1C] px-4 py-3 text-sm focus:outline-none focus:border-[#ec3145] transition-colors duration-300 placeholder-[#1C1C1C]/25" placeholder={cms.form_placeholder_phone} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/40">{cms.form_label_email}</label>
                  <input type="email" name="email" required className="bg-white border border-[#1C1C1C]/10 text-[#1C1C1C] px-4 py-3 text-sm focus:outline-none focus:border-[#ec3145] transition-colors duration-300 placeholder-[#1C1C1C]/25" placeholder={cms.form_placeholder_email} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/40">{cms.form_label_message}</label>
                  <textarea name="message" rows={3} maxLength={500} className="bg-white border border-[#1C1C1C]/10 text-[#1C1C1C] px-4 py-3 text-sm focus:outline-none focus:border-[#ec3145] transition-colors duration-300 resize-none placeholder-[#1C1C1C]/25" placeholder={cms.form_placeholder_message}></textarea>
                </div>
                <button type="submit" disabled={loading} className="bg-[#1C1C1C] text-white text-[11px] font-bold tracking-[0.25em] uppercase py-4 hover:bg-[#ec3145] transition-colors duration-300 cursor-pointer disabled:opacity-50 whitespace-nowrap mt-2">
                  {loading ? cms.form_btn_loading : cms.form_btn_label}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
